import React from "react";
import { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { IAddProductInput, IAttribute } from "muminsmart-utils";
import { Copy, Trash2 } from "lucide-react";
import VariantCardFields from "./VariantCardFields";

const VIRTUALIZE_THRESHOLD = 20;
const ESTIMATED_VARIANT_CARD_HEIGHT = 520;
const VARIANT_LIST_GAP = 32;

interface VariantListHandlers {
  onCopyVariant: (index: number) => void;
  onRequestDeleteVariant: (index: number) => void;
  onGenerateSku: (index: number) => void;
  onAddBulkPrice: (index: number) => void;
  onDeleteBulkPrice: (variantIndex: number, bulkPriceIndex: number) => void;
}

interface VariantListCardProps extends VariantListHandlers {
  form: UseFormReturn<IAddProductInput>;
  index: number;
  variantAttributes: Array<{ attribute: IAttribute; value: string }>;
}

const VariantListCard: React.FC<VariantListCardProps> = ({
  form,
  index,
  variantAttributes,
  onCopyVariant,
  onRequestDeleteVariant,
  onGenerateSku,
  onAddBulkPrice,
  onDeleteBulkPrice,
}) => {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex w-full items-center justify-between gap-2 border-b pb-2">
        <h1 className="font-medium">Variant {index + 1}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onCopyVariant(index)}
            aria-label={`Copy variant ${index + 1}`}
            className="rounded-md border border-primary/30 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5"
          >
            <Copy size={14} />
          </button>
          <button
            type="button"
            onClick={() => onRequestDeleteVariant(index)}
            aria-label={`Delete variant ${index + 1}`}
            className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <VariantCardFields
        form={form}
        index={index}
        variantAttributes={variantAttributes}
        onGenerateSku={onGenerateSku}
        onAddBulkPrice={onAddBulkPrice}
        onDeleteBulkPrice={onDeleteBulkPrice}
      />
    </div>
  );
};

interface VirtualizedVariantListProps extends VariantListHandlers {
  form: UseFormReturn<IAddProductInput>;
  fields: FieldArrayWithId<IAddProductInput, "variants", "id">[];
  variantAttributes: Array<{ attribute: IAttribute; value: string }>;
}

interface VirtualizedVariantRowsProps {
  fields: VirtualizedVariantListProps["fields"];
  cardProps: Omit<VariantListCardProps, "index">;
}

const VirtualizedVariantRows: React.FC<VirtualizedVariantRowsProps> = ({
  fields,
  cardProps,
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);

  React.useLayoutEffect(() => {
    const updateScrollMargin = () => {
      setScrollMargin(listRef.current?.offsetTop ?? 0);
    };

    updateScrollMargin();
    window.addEventListener("resize", updateScrollMargin);

    return () => window.removeEventListener("resize", updateScrollMargin);
  }, [fields.length]);

  const virtualizer = useWindowVirtualizer({
    count: fields.length,
    estimateSize: () => ESTIMATED_VARIANT_CARD_HEIGHT,
    overscan: 3,
    gap: VARIANT_LIST_GAP,
    scrollMargin,
  });

  return (
    <div ref={listRef} className="w-full">
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const field = fields[virtualItem.index];

          if (!field) {
            return null;
          }

          return (
            <div
              key={field.id}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full"
              style={{
                transform: `translateY(${virtualItem.start - scrollMargin}px)`,
              }}
            >
              <VariantListCard index={virtualItem.index} {...cardProps} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VirtualizedVariantList: React.FC<VirtualizedVariantListProps> = ({
  form,
  fields,
  variantAttributes,
  onCopyVariant,
  onRequestDeleteVariant,
  onGenerateSku,
  onAddBulkPrice,
  onDeleteBulkPrice,
}) => {
  const cardProps = {
    form,
    variantAttributes,
    onCopyVariant,
    onRequestDeleteVariant,
    onGenerateSku,
    onAddBulkPrice,
    onDeleteBulkPrice,
  };

  if (fields.length <= VIRTUALIZE_THRESHOLD) {
    return (
      <div className="flex w-full flex-col gap-8">
        {fields.map((field, index) => (
          <VariantListCard key={field.id} index={index} {...cardProps} />
        ))}
      </div>
    );
  }

  return <VirtualizedVariantRows fields={fields} cardProps={cardProps} />;
};

export default VirtualizedVariantList;
